import React from 'react';
import PropTypes from 'prop-types';
import StatusIcon from './StatusIcon';

/**
 * ChatMessage Component
 * 
 * Renders WhatsApp chat messages with three distinct types:
 * 1. Inbound (Customer → Agent) - Blue Grey background, left aligned
 * 2. Outbound (Agent → Customer) - Green background, right aligned with status
 * 3. Internal Note (Agent → Internal) - Orange background, right aligned with note label
 * 
 * Following exact Figma design specifications for colors, typography, and layout
 */
const ChatMessage = ({ message, messageType = 'inbound' }) => {
  // Determine message styling based on type
  const getMessageStyles = () => {
    switch (messageType) {
      case 'inbound':
        return {
          containerAlignment: 'flex-start',
          backgroundColor: '#ECEFF1', // Blue Grey 50
          showStatus: false,
          showAgentInfo: false,
          showNoteLabel: false
        };
      
      case 'outbound':
        return {
          containerAlignment: 'flex-end',
          backgroundColor: '#E8F5E9', // Green 50
          showStatus: true,
          showAgentInfo: true,
          showNoteLabel: false
        };
      
      case 'internal_note':
        return {
          containerAlignment: 'flex-end',
          backgroundColor: '#FFF3E0', // Orange 50
          showStatus: true,
          showAgentInfo: false, // Agent name will be shown in note label
          showNoteLabel: true
        };
      
      default:
        return {
          containerAlignment: 'flex-start',
          backgroundColor: '#ECEFF1',
          showStatus: false,
          showAgentInfo: false,
          showNoteLabel: false
        };
    }
  };

  const styles = getMessageStyles();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      justifyContent: styles.containerAlignment,
      alignItems: 'flex-end',
      alignSelf: 'stretch',
      gap: '5px',
      padding: messageType === 'inbound' ? '10px 20px 10px 30px' : '10px 30px 10px 20px',
      width: '100%'
    }}>
      {/* Status Icon - only for outbound and internal notes */}
      {styles.showStatus && (
        <StatusIcon 
          status={message.status || 'pending'}
          style={{
            flexShrink: 0,
            marginBottom: '4px'
          }}
        />
      )}

      {/* Message Bubble */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '15px 20px',
        backgroundColor: styles.backgroundColor,
        borderRadius: '10px',
        maxWidth: '70%',
        minWidth: '100px'
      }}>
        {/* Message Content Frame */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '5px'
        }}>
          {/* Message Text Frame */}
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            alignSelf: 'stretch',
            gap: '2px'
          }}>
            <span style={{
              fontFamily: 'Poppins',
              fontWeight: '400',
              fontSize: '14px',
              lineHeight: '1.5em',
              textAlign: 'left',
              color: '#344054', // Text color
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap'
            }}>
              {message.text || 'No message content'}
            </span>
          </div>

          {/* Metadata Frame - Timestamp and additional info */}
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignSelf: 'stretch',
            gap: '5px',
            alignItems: 'center'
          }}>
            {/* Timestamp */}
            <span style={{
              fontFamily: 'Poppins',
              fontWeight: '400',
              fontSize: '12px',
              lineHeight: '1.5em',
              textAlign: messageType === 'inbound' ? 'right' : 'left',
              color: '#737C8A' // Grey 2
            }}>
              {message.timestamp}
            </span>

            {/* Agent Info for outbound messages */}
            {styles.showAgentInfo && message.agentName && messageType === 'outbound' && (
              <span style={{
                fontFamily: 'Poppins',
                fontWeight: '400',
                fontSize: '12px',
                lineHeight: '1.5em',
                textAlign: 'right',
                color: '#A5D6A7' // Green 200
              }}>
                Replied by Agent - {message.agentName}
              </span>
            )}

            {/* Internal Note Label with Agent Name */}
            {styles.showNoteLabel && (
              <span style={{
                fontFamily: 'Poppins',
                fontWeight: '400',
                fontSize: '12px',
                lineHeight: '1.5em',
                textAlign: 'right',
                color: '#FFB74D' // Orange 300
              }}>
                {message.agentName ? `Internal note by ${message.agentName}` : 'Internal note'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ChatMessage.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    text: PropTypes.string,
    timestamp: PropTypes.string.isRequired,
    fullTimestamp: PropTypes.instanceOf(Date),
    agentName: PropTypes.string,
    status: PropTypes.oneOf(['sent', 'delivered', 'read', 'failed', 'pending']),
    messageType: PropTypes.string,
    messageId: PropTypes.string
  }).isRequired,
  messageType: PropTypes.oneOf(['inbound', 'outbound', 'internal_note']).isRequired
};

ChatMessage.defaultProps = {
  messageType: 'inbound'
};

export default React.memo(ChatMessage);